<template>
  <div class="brand-management">
    <el-card class="box-card">
      <template #header>
        <div class="card-header">
          <h2>品牌管理</h2>
          <div class="header-actions">
            <el-button type="primary" @click="handleCreate" :disabled="!canEditBrands">
              <el-icon><Plus /></el-icon> 新增品牌
            </el-button>
            <el-button @click="showColumnConfig = true">
              <el-icon><Setting /></el-icon> 列设置
            </el-button>
          </div>
        </div>
      </template>

      <!-- 搜索和筛选区域 -->
      <div class="search-section">
        <el-input
          v-model="searchKeyword"
          placeholder="搜索品牌名称或代码"
          @keyup.enter="loadData"
          clearable
          @clear="loadData"
        >
          <template #append>
            <el-button @click="loadData">
              <el-icon><Search /></el-icon>
            </el-button>
          </template>
        </el-input>

        <el-select
          v-model="filterActive"
          placeholder="状态筛选"
          @change="loadData"
          clearable
        >
          <el-option label="全部" value="" />
          <el-option label="启用" :value="true" />
          <el-option label="禁用" :value="false" />
        </el-select>

        <el-button @click="resetFilters">
          <el-icon><Refresh /></el-icon> 重置
        </el-button>
      </div>

      <!-- 数据表格 - 使用动态列渲染 -->
      <el-table
        ref="tableRef"
        :data="tableData"
        v-loading="loading"
        :default-sort="{ prop: 'created_at', order: 'descending' }"
        style="width: 100%"
        border
        resizable
        @header-dragend="handleColumnResize"
        :key="tableKey"
      >
        <!-- 动态渲染列 -->
        <el-table-column 
          v-for="column in visibleColumns"
          :key="column.prop"
          :prop="column.prop"
          :label="column.label"
          :width="column.visible ? columnWidths[column.prop] : 0"
          :sortable="column.sortable"
          :resizable="column.resizable"
          :fixed="column.fixed"
          :show-overflow-tooltip="column.showOverflowTooltip"
        >
          <template #default="{ row }">
            <!-- 状态列特殊渲染 -->
            <template v-if="column.prop === 'is_active'">
              <el-tag :type="row.is_active ? 'success' : 'danger'">
                {{ row.is_active ? '启用' : '禁用' }}
              </el-tag>
            </template>
            
            <!-- 日期列特殊渲染 -->
            <template v-else-if="column.prop === 'created_at' || column.prop === 'updated_at'">
              {{ formatDate(row[column.prop]) }}
            </template>
            
            <!-- 普通列渲染 -->
            <template v-else>
              {{ row[column.prop] }}
            </template>
          </template>
        </el-table-column>

        <!-- 操作列始终显示 -->
        <el-table-column 
          label="操作" 
          :width="columnWidths.operations" 
          fixed="right"
          resizable
        >
          <template #default="{ row }">
            <el-button size="small" @click="handleEdit(row)" :disabled="!canEditBrands">编辑</el-button>
            <el-button
              size="small"
              :type="row.is_active ? 'danger' : 'success'"
              @click="handleToggleStatus(row)"
              :disabled="!canEditBrands"
            >
              {{ row.is_active ? '禁用' : '启用' }}
            </el-button>
            <el-button size="small" type="danger" @click="handleDelete(row)" :disabled="!canEditBrands">
              删除
            </el-button>
          </template>
        </el-table-column>
      </el-table>

      <!-- 分页控件 -->
      <div class="pagination-section">
        <el-pagination
          v-model:current-page="pagination.currentPage"
          v-model:page-size="pagination.pageSize"
          :total="pagination.total"
          :page-sizes="[10, 20, 50, 100]"
          layout="total, sizes, prev, pager, next, jumper"
          @size-change="loadData"
          @current-change="loadData"
        />
      </div>
    </el-card>

    <!-- 新增/编辑对话框 -->
    <el-dialog
      :title="dialogTitle"
      v-model="showDialog"
      :width="isEditing ? '640px' : '500px'"
      @closed="resetForm"
    >
      <el-tabs v-if="isEditing" v-model="brandFormTab" type="card">
        <el-tab-pane label="基本信息" name="basic">
          <el-form
            :model="formData"
            :rules="formRules"
            ref="formRef"
            label-width="100px"
          >
            <el-form-item label="品牌代码" prop="brand_code">
              <el-input 
                v-model="formData.brand_code" 
                placeholder="请输入品牌代码" 
                maxlength="20"
              />
            </el-form-item>
            <el-form-item label="品牌名称" prop="brand_name">
              <el-input 
                v-model="formData.brand_name" 
                placeholder="请输入品牌名称" 
                maxlength="100"
              />
            </el-form-item>
            <el-form-item label="公司名称" prop="company_name">
              <el-input 
                v-model="formData.company_name" 
                placeholder="请输入公司名称" 
                maxlength="200"
              />
            </el-form-item>
            <el-form-item label="状态" prop="is_active">
              <el-switch v-model="formData.is_active" />
            </el-form-item>
          </el-form>
        </el-tab-pane>
        <el-tab-pane label="季度报表默认" name="seasonReport">
          <el-form label-width="160px">
            <el-form-item label="Supplier/Vendor ID">
              <el-input v-model="seasonProfile.supplier_vendor_id" placeholder="如 MK：SV0245" :disabled="!canEditBrands" />
            </el-form-item>
            <el-form-item label="Supplier/Vendor Name">
              <el-input v-model="seasonProfile.supplier_vendor_name" placeholder="默认 IVANDA" :disabled="!canEditBrands" />
            </el-form-item>
            <el-form-item label="Supplier COO">
              <el-input v-model="seasonProfile.supplier_coo" placeholder="默认 CHINA" :disabled="!canEditBrands" />
            </el-form-item>
            <el-form-item label="Material COO">
              <el-input v-model="seasonProfile.material_coo" placeholder="默认 CHINA" :disabled="!canEditBrands" />
            </el-form-item>
            <el-form-item label="Shipment Term">
              <el-input v-model="seasonProfile.default_shipment_term" placeholder="默认 EXW" :disabled="!canEditBrands" />
            </el-form-item>
            <el-form-item>
              <el-button type="primary" :disabled="!canEditBrands" :loading="savingSeasonProfile" @click="saveSeasonProfile">
                保存报表默认
              </el-button>
            </el-form-item>
          </el-form>
        </el-tab-pane>
      </el-tabs>

      <el-form
        v-else
        :model="formData"
        :rules="formRules"
        ref="formRef"
        label-width="100px"
      >
        <el-form-item label="品牌代码" prop="brand_code">
          <el-input 
            v-model="formData.brand_code" 
            placeholder="请输入品牌代码" 
            maxlength="20"
          />
        </el-form-item>
        <el-form-item label="品牌名称" prop="brand_name">
          <el-input 
            v-model="formData.brand_name" 
            placeholder="请输入品牌名称" 
            maxlength="100"
          />
        </el-form-item>
        <el-form-item label="公司名称" prop="company_name">
          <el-input 
            v-model="formData.company_name" 
            placeholder="请输入公司名称" 
            maxlength="200"
          />
        </el-form-item>
        <el-form-item label="状态" prop="is_active">
          <el-switch v-model="formData.is_active" />
        </el-form-item>
      </el-form>
      <template #footer>
        <span class="dialog-footer">
          <el-button @click="showDialog = false">取消</el-button>
          <el-button type="primary" @click="submitForm" :disabled="!canEditBrands">确认</el-button>
        </span>
      </template>
    </el-dialog>

    <!-- 列设置对话框 -->
    <ColumnConfigDialog
      :show="showColumnConfig"
      :column-config="columnConfig"
      :page-title="'品牌管理'"
      storage-key="brand_management"
      :local-storage-key="brandColumnConfigFullStorageKey"
      @update:show="showColumnConfig = $event"
      @save="handleColumnSave"
      @reset-widths="handleColumnResetWidths"
      @reset-all="handleColumnResetAll"
      @select-all="handleColumnSelectAll"
      @unselect-all="handleColumnUnselectAll"
      @config-change="handleColumnConfigChange"
    />
  </div>
</template>

<script setup>
import { ref, reactive, onMounted, computed, nextTick } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { ElMessage, ElMessageBox } from 'element-plus'
import { Plus, Search, Refresh, Setting } from '@element-plus/icons-vue'
import ColumnConfigDialog from '@/components/ColumnConfigDialog.vue'
import useColumnWidth from '@/composables/useColumnWidth'
import { getUserScopedStorageKey } from '@/utils/userScopedStorage'
import { getBrandSeasonProfile, saveBrandSeasonProfile } from '@/api/reports'

// API导入
import {
  getBrands,
  createBrand,
  updateBrand,
  deleteBrand,
  toggleBrandStatus
} from '@/api/brands.js'
import { hasPermission } from '@/utils/permission'
import { setOrderCreatePendingPick } from '@/utils/orderCreateDraft.js'
import { useTabs } from '@/composables/useTabs'

const route = useRoute()
const router = useRouter()

// 默认列配置
const defaultColumnConfig = [
  { prop: 'brand_id', label: 'ID', visible: true, width: 80, sortable: true, resizable: true },
  { prop: 'brand_code', label: '品牌代码', visible: true, width: 120, sortable: true, resizable: true },
  { prop: 'brand_name', label: '品牌名称', visible: true, width: 150, sortable: true, resizable: true },
  { prop: 'company_name', label: '公司名称', visible: true, width: 180, resizable: true },
  { prop: 'is_active', label: '状态', visible: true, width: 100 },
  { prop: 'created_at', label: '创建时间', visible: true, width: 160, sortable: true },
  { prop: 'updated_at', label: '更新时间', visible: true, width: 160, sortable: true }
]

// 默认列宽配置
const defaultColumnWidths = {
  brand_id: 80,
  brand_code: 120,
  brand_name: 150,
  company_name: 180,
  is_active: 100,
  created_at: 160,
  updated_at: 160,
  operations: 200
}

// 列映射配置
const columnMap = {
  'ID': 'brand_id',
  '品牌代码': 'brand_code',
  '品牌名称': 'brand_name',
  '公司名称': 'company_name',
  '状态': 'is_active',
  '创建时间': 'created_at',
  '更新时间': 'updated_at',
  '操作': 'operations'
}

// 使用列宽管理组合式函数
const { 
  tableRef, 
  columnWidths, 
  handleColumnResize, 
  resetColumnWidths,
  getColumnConfig 
} = useColumnWidth('brand_management', defaultColumnWidths, columnMap)

const brandColumnConfigFullStorageKey = `${getUserScopedStorageKey('brand_management')}_columns_config`

const loading = ref(false)
const searchKeyword = ref('')
const filterActive = ref('')
const showDialog = ref(false)
const showColumnConfig = ref(false)
const formRef = ref()
const isEditing = ref(false)
const brandFormTab = ref('basic')
const savingSeasonProfile = ref(false)
const seasonProfile = reactive({
  supplier_vendor_id: '',
  supplier_vendor_name: 'IVANDA',
  supplier_coo: 'CHINA',
  material_coo: 'CHINA',
  default_shipment_term: 'EXW',
})
const tableData = ref([])
const columnConfig = ref([])
const tableKey = ref(0) // 用于强制重新渲染表格
const currentUser = ref(JSON.parse(localStorage.getItem('user') || '{}'))
const canEditBrands = computed(() => hasPermission(currentUser.value, 'brands.edit'))

// 分页配置
const pagination = reactive({
  currentPage: 1,
  pageSize: 10,
  total: 0
})

// 表单数据
const formData = reactive({
  brand_id: '',
  brand_code: '',
  brand_name: '',
  company_name: '',
  is_active: true
})

// 表单验证规则
const formRules = {
  brand_code: [
    { required: true, message: '请输入品牌代码', trigger: 'blur' },
    { min: 1, max: 20, message: '长度在 1 到 20 个字符', trigger: 'blur' }
  ],
  brand_name: [
    { required: true, message: '请输入品牌名称', trigger: 'blur' },
    { min: 1, max: 100, message: '长度在 1 到 100 个字符', trigger: 'blur' }
  ],
  company_name: [
    { required: true, message: '请输入公司名称', trigger: 'blur' },
    { min: 1, max: 200, message: '长度在 1 到 200 个字符', trigger: 'blur' }
  ]
}

// 计算属性
const dialogTitle = computed(() => 
  isEditing.value ? '编辑品牌' : '新增品牌'
)

// 可见列计算属性
const visibleColumns = computed(() => {
  return columnConfig.value.filter(col => col.visible)
})

// 格式化日期
const formatDate = (dateString) => {
  if (!dateString) return ''
  const date = new Date(dateString)
  return date.toLocaleString('zh-CN')
}

// 强制表格重新渲染
const forceTableRerender = () => {
  tableKey.value += 1
}

// 生命周期
onMounted(() => {
  // 初始化列配置
  columnConfig.value = getColumnConfig(defaultColumnConfig)
  loadData()
  if (route.query.fromOrderCreate === '1') {
    nextTick(() => {
      if (canEditBrands.value) handleCreate()
    })
  }
})

// 加载数据
const loadData = async () => {
  loading.value = true
  try {
    const params = {
      page: pagination.currentPage,
      per_page: pagination.pageSize,
      search: searchKeyword.value,
      is_active: filterActive.value
    }

    const response = await getBrands(params)
    tableData.value = response.data
    pagination.total = response.total
  } catch (error) {
    ElMessage.error('加载数据失败: ' + (error.response?.data?.message || error.message))
  } finally {
    loading.value = false
  }
}

// 重置筛选条件
const resetFilters = () => {
  searchKeyword.value = ''
  filterActive.value = ''
  pagination.currentPage = 1
  loadData()
}

// 打开新增对话框
const handleCreate = () => {
  if (!canEditBrands.value) {
    ElMessage.warning('你没有新增品牌权限')
    return
  }
  isEditing.value = false
  brandFormTab.value = 'basic'
  showDialog.value = true
}

// 打开编辑对话框
const handleEdit = (row) => {
  if (!canEditBrands.value) {
    ElMessage.warning('你没有编辑品牌权限')
    return
  }
  isEditing.value = true
  brandFormTab.value = 'basic'
  Object.assign(formData, row)
  showDialog.value = true
  loadSeasonProfile(row.brand_id)
}

const loadSeasonProfile = async (brandId) => {
  Object.assign(seasonProfile, {
    supplier_vendor_id: '',
    supplier_vendor_name: 'IVANDA',
    supplier_coo: 'CHINA',
    material_coo: 'CHINA',
    default_shipment_term: 'EXW',
  })
  if (!brandId) return
  try {
    const res = await getBrandSeasonProfile({ brand_id: brandId })
    const data = res?.data || null
    if (!data) return
    seasonProfile.supplier_vendor_id = data.supplier_vendor_id || ''
    seasonProfile.supplier_vendor_name = data.supplier_vendor_name || 'IVANDA'
    seasonProfile.supplier_coo = data.supplier_coo || 'CHINA'
    seasonProfile.material_coo = data.material_coo || 'CHINA'
    seasonProfile.default_shipment_term = data.default_shipment_term || 'EXW'
  } catch (e) {
    console.warn(e)
  }
}

const saveSeasonProfile = async () => {
  if (!formData.brand_id) return
  savingSeasonProfile.value = true
  try {
    await saveBrandSeasonProfile({
      brand_id: formData.brand_id,
      ...seasonProfile,
    })
    ElMessage.success('季度报表默认已保存')
  } catch (e) {
    ElMessage.error(e.response?.data?.message || e.message || '保存失败')
  } finally {
    savingSeasonProfile.value = false
  }
}

// 提交表单
const submitForm = async () => {
  if (!canEditBrands.value) {
    ElMessage.warning('你没有编辑品牌权限')
    return
  }
  if (!formRef.value) return

  try {
    await formRef.value.validate()

    if (isEditing.value) {
      await updateBrand(formData.brand_id, formData)
      ElMessage.success('更新成功')
    } else {
      const response = await createBrand(formData)
      ElMessage.success('创建成功')
      showDialog.value = false
      loadData()
      if (route.query.fromOrderCreate === '1') {
        const id = response?.data?.brand_id
        if (id != null) setOrderCreatePendingPick({ brand_id: id })
        try {
          await ElMessageBox.confirm(
            '品牌已保存。是否返回「订单管理」继续录入当前订单？',
            '返回订单',
            {
              type: 'success',
              confirmButtonText: '返回订单',
              cancelButtonText: '留在本页'
            }
          )
          await router.push({ path: '/orders', query: { resumeOrderDraft: '1' } })
          await nextTick()
          useTabs().removeTab('/brands')
        } catch {
          /* 留在品牌管理：不关标签 */
        }
        return
      }
    }

    showDialog.value = false
    loadData()
  } catch (error) {
    if (error instanceof Error) {
      ElMessage.error('操作失败: ' + error.message)
    }
    // 验证失败不会抛出错误，所以这里不需要处理
  }
}

// 删除数据
const handleDelete = async (row) => {
  if (!canEditBrands.value) {
    ElMessage.warning('你没有删除品牌权限')
    return
  }
  try {
    await ElMessageBox.confirm(
      `确定要删除品牌 "${row.brand_name}" 吗？此操作不可恢复。`,
      '警告',
      { 
        type: 'warning',
        confirmButtonText: '确定',
        cancelButtonText: '取消'
      }
    )

    await deleteBrand(row.brand_id)
    ElMessage.success('删除成功')
    loadData()
  } catch (error) {
    if (error !== 'cancel') {
      const data = error.response?.data
      const msg = data?.message || error.message || '未知错误'
      if (data?.hints?.length || String(msg).includes('【处理指引】')) {
        ElMessageBox.alert(msg, '无法删除品牌', { confirmButtonText: '知道了' })
      } else {
        ElMessage.error('删除失败: ' + msg)
      }
    }
  }
}

// 切换状态
const handleToggleStatus = async (row) => {
  if (!canEditBrands.value) {
    ElMessage.warning('你没有修改品牌状态权限')
    return
  }
  try {
    await toggleBrandStatus(row.brand_id, { is_active: !row.is_active })
    ElMessage.success('状态更新成功')
    loadData()
  } catch (error) {
    ElMessage.error('状态更新失败: ' + (error.response?.data?.message || error.message))
  }
}

// 重置表单
const resetForm = () => {
  if (formRef.value) {
    formRef.value.resetFields()
  }
  
  Object.keys(formData).forEach(key => {
    if (key !== 'is_active') {
      formData[key] = ''
    } else {
      formData[key] = true
    }
  })
  
  isEditing.value = false
  brandFormTab.value = 'basic'
}

// 列设置相关方法
const handleColumnSave = (newConfig) => {
  columnConfig.value = newConfig
  // 更新列宽配置
  newConfig.forEach(col => {
    if (columnWidths[col.prop] !== undefined) {
      columnWidths[col.prop] = col.width
    }
  })
  // 强制表格重新渲染以应用新的列配置
  forceTableRerender()
  ElMessage.success('列设置已保存')
}

const handleColumnResetWidths = () => {
  resetColumnWidths()
  // 同时重置配置中的宽度
  columnConfig.value.forEach(col => {
    const defaultCol = defaultColumnConfig.find(c => c.prop === col.prop)
    if (defaultCol) {
      col.width = defaultCol.width
    }
  })
  forceTableRerender()
}

const handleColumnResetAll = () => {
  resetColumnWidths()
  columnConfig.value = [...defaultColumnConfig]
  forceTableRerender()
}

const handleColumnSelectAll = () => {
  columnConfig.value.forEach(col => {
    col.visible = true
  })
  forceTableRerender()
}

const handleColumnUnselectAll = () => {
  columnConfig.value.forEach(col => {
    if (col.prop !== 'operations') {
      col.visible = false
    }
  })
  forceTableRerender()
}

const handleColumnConfigChange = (newConfig) => {
  columnConfig.value = newConfig
  // 实时应用列配置变化
  forceTableRerender()
}
</script>

<style scoped>
.brand-management {
  padding: 20px;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.header-actions {
  display: flex;
  gap: 10px;
  align-items: center;
}

.search-section {
  margin-bottom: 20px;
  display: flex;
  gap: 10px;
  align-items: center;
}

.pagination-section {
  margin-top: 20px;
  display: flex;
  justify-content: flex-end;
}

/* 确保表头文本不换行 */
:deep(.el-table .el-table__header-wrapper .el-table__header th .cell) {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>